import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, User } from 'lucide-react';
import { formatPlayerName } from '../lib/nameUtils';

export default function HallOfFameBanner({
  profiles = [],
  hallOfFameSnapshots = [],
  onViewCard,
  onGoToRanking,
  style = {}
}) {
  const [showHofModal, setShowHofModal] = useState(false);
  const [hofLevelTab, setHofLevelTab] = useState('Principiante');

  // Group and find top 1 per level (using snapshots primarily, or profiles as fallback)
  const top1ByLevel = useMemo(() => {
    const levelsToDisplay = ['Principiante', 'Intermedio', 'Avanzado'];
    const results = {};

    if (hallOfFameSnapshots && hallOfFameSnapshots.length > 0) {
      levelsToDisplay.forEach(lvl => {
        const topItem = hallOfFameSnapshots.find(item => 
          item.level === lvl && 
          item.level_rank === 1 && 
          item.profile_type !== 'equipo'
        );
        if (topItem) {
          results[lvl] = {
            id: topItem.profile_id,
            name: topItem.name,
            show_full_name: topItem.show_full_name,
            profile_type: topItem.profile_type,
            image: topItem.image_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${topItem.profile_id}`,
            xp: topItem.xp,
            level: topItem.level,
            rank: topItem.rank_position
          };
        }
      });
    }

    // Fallback if snapshots are empty or missing a level
    levelsToDisplay.forEach(lvl => {
      if (!results[lvl]) {
        const playersOfLevel = profiles
          .filter(p => p.type !== 'equipo' && ((p.xp >= 6000 && lvl === 'Leyenda') || 
                       (p.xp >= 3000 && p.xp < 6000 && lvl === 'Avanzado') ||
                       (p.xp >= 1500 && p.xp < 3000 && lvl === 'Intermedio') ||
                       (p.xp >= 500 && p.xp < 1500 && lvl === 'Principiante') ||
                       (p.xp < 500 && lvl === 'Novato')))
          .sort((a, b) => b.xp - a.xp);
        
        if (playersOfLevel.length > 0) {
          const topPlayer = playersOfLevel[0];
          results[lvl] = {
            id: topPlayer.id,
            name: topPlayer.name,
            show_full_name: topPlayer.show_full_name,
            profile_type: topPlayer.type,
            image: topPlayer.image || `https://api.dicebear.com/7.x/shapes/svg?seed=${topPlayer.id}`,
            xp: topPlayer.xp,
            level: lvl,
            rank: 1
          };
        }
      }
    });

    return results;
  }, [hallOfFameSnapshots, profiles]);

  const lastUpdateDateStr = useMemo(() => {
    if (hallOfFameSnapshots && hallOfFameSnapshots.length > 0) {
      const date = new Date(hallOfFameSnapshots[0].snapshot_date);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, [hallOfFameSnapshots]);

  const LEVELS = [
    { key: 'Principiante', color: '#60A5FA', rgb: '96,165,250', hsl: '210, 100%, 70%' },
    { key: 'Intermedio',   color: '#34D399', rgb: '52,211,153', hsl: '140, 100%, 70%' },
    { key: 'Avanzado',     color: '#FBBF24', rgb: '251,191,36', hsl: '45, 100%, 55%'  },
    { key: 'Leyenda',      color: '#F472B6', rgb: '244,114,182', hsl: '330, 85%, 68%' },
  ];

  const currentLevel = LEVELS.find(l => l.key === hofLevelTab) || LEVELS[0];

  const playersInTab = useMemo(() => {
    return (hallOfFameSnapshots || [])
      .filter(s => s.level === hofLevelTab && s.profile_type !== 'equipo')
      .sort((a, b) => (a.level_rank || 999) - (b.level_rank || 999));
  }, [hallOfFameSnapshots, hofLevelTab]);

  const rankMedal = (pos) => {
    if (pos === 1) return { emoji: '🥇', color: '#FFD700' };
    if (pos === 2) return { emoji: '🥈', color: '#C0C0C0' };
    if (pos === 3) return { emoji: '🥉', color: '#CD7F32' };
    return { emoji: `#${pos}`, color: 'rgba(255,255,255,0.4)' };
  };

  const handlePlayerClick = (profileId, fallbackData) => {
    if (onViewCard) {
      setShowHofModal(false);
      // Attempt to find the full profile from our profiles prop to guarantee all stats, position, gender render correctly
      const fullProfile = profiles.find(p => p.id === profileId);
      if (fullProfile) {
        onViewCard(fullProfile);
      } else {
        // Fallback to minimal data if not loaded or not in profiles prop
        onViewCard({
          id: profileId,
          type: 'jugador',
          name: fallbackData.name,
          image: fallbackData.image || `https://api.dicebear.com/7.x/shapes/svg?seed=${profileId}`,
          xp: fallbackData.xp,
          level: fallbackData.level
        });
      }
    }
  };

  return (
    <>
      <div className="glass" style={{
        padding: '1.5rem',
        borderRadius: '1.5rem',
        border: '1px solid rgba(255, 215, 0, 0.15)',
        background: 'linear-gradient(135deg, rgba(255,215,0,0.04) 0%, rgba(0,0,0,0.4) 100%)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        ...style
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 900, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Trophy size={20} color="var(--primary)" /> Hall of Fame
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)', padding: '0.15rem 0.5rem', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.08)' }}>
              Actualizado {lastUpdateDateStr}
            </span>
          </h3>
          <button
            onClick={() => setShowHofModal(true)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '99rem',
              background: 'rgba(212,255,0,0.1)',
              border: '1px solid rgba(212,255,0,0.3)',
              color: 'var(--primary)',
              fontSize: '0.8rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,255,0,0.18)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,255,0,0.1)'; }}
          >
            🏆 Ver ranking completo
          </button>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
          gap: '0.75rem'
        }}>
          {['Principiante', 'Intermedio', 'Avanzado'].map((lvl) => {
            const player = top1ByLevel[lvl];
            const levelColors = {
              'Principiante': { color: '#60A5FA', rgb: '96,165,250' },
              'Intermedio':   { color: '#34D399', rgb: '52,211,153' },
              'Avanzado':     { color: '#FBBF24', rgb: '251,191,36' }
            }[lvl];

            if (!player) return (
              <div key={lvl} style={{
                padding: '0.8rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255,255,255,0.05)',
                background: 'rgba(255,255,255,0.02)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.4rem',
                minHeight: '80px',
                justifyContent: 'center'
              }}>
                <User size={20} style={{ opacity: 0.2 }} />
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{lvl}</span>
              </div>
            );

            return (
              <motion.div
                key={lvl}
                whileHover={{ y: -3, scale: 1.02 }}
                onClick={() => handlePlayerClick(player.id, player)}
                style={{
                  padding: '0.85rem',
                  borderRadius: '1rem',
                  border: `1px solid rgba(${levelColors.rgb}, 0.2)`,
                  background: `linear-gradient(135deg, rgba(${levelColors.rgb}, 0.08) 0%, rgba(0,0,0,0.4) 100%)`,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  transition: 'all 0.2s',
                  overflow: 'hidden'
                }}
              >
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <img
                    src={player.image}
                    alt={player.name}
                    style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${levelColors.color}` }}
                  />
                  <div style={{
                    position: 'absolute', bottom: '-4px', right: '-4px',
                    background: levelColors.color, color: '#000',
                    fontSize: '0.6rem', fontWeight: 900,
                    width: '16px', height: '16px', borderRadius: '50%',
                    display: 'grid', placeItems: 'center'
                  }}>1</div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: '0.6rem', fontWeight: 900, color: levelColors.color, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>{lvl}</span>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff' }}>
                    {formatPlayerName(player.name, player.show_full_name, false, player.profile_type)}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>⚡ {player.xp} XP</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {showHofModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowHofModal(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.92)',
              backdropFilter: 'blur(16px)',
              zIndex: 3000,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '1rem',
              overflowY: 'auto'
            }}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 24 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 24 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'linear-gradient(160deg, rgba(18,18,22,0.98) 0%, rgba(10,10,14,1) 100%)',
                border: `1px solid rgba(${currentLevel.rgb}, 0.25)`,
                borderRadius: '2rem',
                padding: '2rem 1.5rem',
                width: '100%',
                maxWidth: '520px',
                boxShadow: `0 0 60px rgba(${currentLevel.rgb}, 0.12), 0 30px 60px rgba(0,0,0,0.5)`,
                marginTop: '1rem',
                marginBottom: '2rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.7rem', fontWeight: 900, margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <Trophy size={26} color="var(--primary)" /> Hall of Fame
                  </h2>
                  <p style={{ color: 'var(--text-muted)', margin: '0.3rem 0 0', fontSize: '0.82rem' }}>
                    Ranking quincenal de jugadores • {lastUpdateDateStr}
                  </p>
                </div>
                <button
                  onClick={() => setShowHofModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    width: '36px', height: '36px',
                    display: 'grid', placeItems: 'center',
                    cursor: 'pointer', color: '#fff',
                    fontSize: '1.1rem', flexShrink: 0
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '1.5rem',
                background: 'rgba(255,255,255,0.04)',
                padding: '0.35rem',
                borderRadius: '1rem',
                flexWrap: 'wrap'
              }}>
                {LEVELS.map(lvl => (
                  <button
                    key={lvl.key}
                    onClick={() => setHofLevelTab(lvl.key)}
                    style={{
                      flex: 1,
                      minWidth: '80px',
                      padding: '0.55rem 0.5rem',
                      borderRadius: '0.75rem',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      transition: 'all 0.2s',
                      background: hofLevelTab === lvl.key
                        ? `rgba(${lvl.rgb}, 0.18)`
                        : 'transparent',
                      color: hofLevelTab === lvl.key ? lvl.color : 'rgba(255,255,255,0.45)',
                      boxShadow: hofLevelTab === lvl.key ? `0 0 12px rgba(${lvl.rgb}, 0.2)` : 'none',
                      border: hofLevelTab === lvl.key ? `1px solid rgba(${lvl.rgb}, 0.35)` : '1px solid transparent'
                    }}
                  >
                    {lvl.key === 'Principiante' ? '🟦' : lvl.key === 'Intermedio' ? '🟩' : lvl.key === 'Avanzado' ? '🟨' : '💜'} {lvl.key}
                  </button>
                ))}
              </div>

              {playersInTab.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-muted)' }}>
                  <User size={40} style={{ opacity: 0.25, marginBottom: '1rem' }} />
                  <p style={{ margin: 0 }}>Todavía no hay jugadores en este nivel con puntos registrados.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {playersInTab.map((player, idx) => {
                    const pos = player.level_rank || (idx + 1);
                    const medal = rankMedal(pos);
                    const isTop3 = pos <= 3;
                    return (
                      <motion.div
                        key={player.profile_id || idx}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.04 }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.9rem',
                          padding: isTop3 ? '1rem 1.1rem' : '0.75rem 1rem',
                          borderRadius: '1.1rem',
                          background: isTop3
                            ? `linear-gradient(135deg, rgba(${currentLevel.rgb}, 0.1) 0%, rgba(0,0,0,0.3) 100%)`
                            : 'rgba(255,255,255,0.03)',
                          border: isTop3
                            ? `1px solid rgba(${currentLevel.rgb}, 0.25)`
                            : '1px solid rgba(255,255,255,0.05)',
                          boxShadow: isTop3 ? `0 4px 20px rgba(${currentLevel.rgb}, 0.08)` : 'none',
                          cursor: onViewCard ? 'pointer' : 'default',
                          transition: 'all 0.2s'
                        }}
                        whileHover={onViewCard ? { scale: 1.01 } : {}}
                        onClick={() => handlePlayerClick(player.profile_id, {
                          name: player.name,
                          image: player.image_url,
                          xp: player.xp,
                          level: player.level
                        })}
                      >
                        <div style={{
                          minWidth: '2.2rem',
                          textAlign: 'center',
                          fontSize: isTop3 ? '1.5rem' : '0.85rem',
                          fontWeight: 900,
                          color: medal.color,
                          flexShrink: 0
                        }}>
                          {isTop3 ? medal.emoji : `#${pos}`}
                        </div>

                        <img
                          src={player.image_url || `https://api.dicebear.com/7.x/shapes/svg?seed=${player.profile_id}`}
                          alt={player.name}
                          style={{
                            width: isTop3 ? '46px' : '38px',
                            height: isTop3 ? '46px' : '38px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: isTop3 ? `2px solid ${currentLevel.color}` : '1px solid rgba(255,255,255,0.15)',
                            flexShrink: 0
                          }}
                        />

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{
                            margin: 0,
                            fontWeight: isTop3 ? 800 : 600,
                            fontSize: isTop3 ? '0.95rem' : '0.88rem',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            color: isTop3 ? '#fff' : 'rgba(255,255,255,0.8)'
                          }}>
                            {formatPlayerName(player.name, player.show_full_name, false, player.profile_type)}
                          </p>
                          <p style={{
                            margin: 0,
                            fontSize: '0.75rem',
                            color: isTop3 ? currentLevel.color : 'rgba(255,255,255,0.4)',
                            fontWeight: 700
                          }}>
                            ⚡ {player.xp?.toLocaleString('es-AR')} XP
                          </p>
                        </div>

                        {isTop3 && (
                          <div style={{
                            width: '50px',
                            height: '4px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '2px',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${Math.min((player.xp / 6000) * 100, 100)}%`,
                              background: currentLevel.color,
                              boxShadow: `0 0 6px ${currentLevel.color}`
                            }} />
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => {
                  setShowHofModal(false);
                  onGoToRanking && onGoToRanking();
                }}
                style={{
                  marginTop: '1.5rem',
                  width: '100%',
                  padding: '0.9rem',
                  borderRadius: '99rem',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                🔍 Ir a Búsqueda para citar jugadores
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
